<script setup lang="ts">
  import {
    onMounted,
    ref,
    inject,
    type ComputedRef,
    provide,
    computed,
    defineAsyncComponent,
  } from 'vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import { FlowWidget } from '@/widgets/Flow'
  import { useUrlParams } from '@/shared/composables'
  import { useHomeUrl } from '@/shared/composables/useHomeUrl'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useI18n } from 'vue-i18n'
  import { useFeaturesStore } from '@/shared/store/featuresStore'
  import { FlowSuccessLoader } from '@/entities/Flow/components/FlowSuccessLoader'

  const FlowNetworkErrorWidgetQC = defineAsyncComponent(
    () => import('@/widgets/Flow/FlowNetworkErrorWidgetQC/FlowNetworkErrorWidgetQC.vue')
  )
  const SuccessInsuredWidget = defineAsyncComponent(
    () => import('@/widgets/SuccessInsuredWidget/SuccessInsuredWidget.vue')
  )
  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { removeUrlParams } = useUrlParams()
  const { getHomeUrl } = useHomeUrl()
  const homeUrl = getHomeUrl()
  const featureStore = useFeaturesStore()
  const isRTL = inject('isRTL') as ComputedRef<boolean>
  const isLoading = ref(true)
  const policyLink = ref('')
  const cyberPolicyLink = ref<string | undefined>()
  const policyNumber = ref('')
  const email = ref(userStore.email || '')
  const error = ref(false)
  const isUserRegistered = ref(false)
  const earnedBonuses = ref(0)
  const homePromocode = ref('')

  const { locale } = useI18n()
  // DownloadAppBanner component from loyalty-components lib uses this
  provide('locale', locale)

  const createHomePromocode = async () => {
    const promocode = await commonStore.createHomePromocode(policyNumber.value)
    homePromocode.value = promocode || ''
  }

  const getPolicyInformation = async () => {
    isLoading.value = true
    error.value = false

    try {
      const result = await commonStore.getPolicy()
      policyNumber.value = result.policyNo
      policyLink.value = result.link
      cyberPolicyLink.value = result.cyberSecurityWordingsLink
      isUserRegistered.value = result.isUserRegistered
      earnedBonuses.value = result.earnedBonuses ?? 0

      removeUrlParams('redirectURL')
      userStore.setPromocode(null, true)

      if (isTravelHomeOfferBannerEnabled.value) {
        await createHomePromocode()
      }
    } catch {
      error.value = true
    } finally {
      isLoading.value = false
    }
  }

  onMounted(() => {
    getPolicyInformation()
  })

  const handleOnBack = () => (window.location.href = homeUrl)

  const lastFlowDirection = userStore.lastFlowDirection || TravelDirection.OUTBOUND

  const isOoredooBannerEnabled = computed(
    () => lastFlowDirection === TravelDirection.OUTBOUND && featureStore.isOoredooEnabled
  )

  const isTravelHomeOfferBannerEnabled = computed(
    () => lastFlowDirection === TravelDirection.OUTBOUND && featureStore.isTravelHomeOfferEnabled
  )

  const isTravelHomeOfferBannerVisible = computed(
    () => isTravelHomeOfferBannerEnabled.value && !!homePromocode.value
  )

  const isHotelBannerEnabled = computed(
    () => lastFlowDirection === TravelDirection.OUTBOUND && featureStore.isQatarBookingOfferEnabled
  )
</script>

<template>
  <FlowWidget
    :steps-count="0"
    :is-rtl="isRTL"
    :with-progress-bar="false"
    class="ds-flow-widget"
    @go-back="handleOnBack"
  >
    <FlowSuccessLoader v-if="isLoading" />
    <template v-else>
      <FlowNetworkErrorWidgetQC v-if="error" :home-url="homeUrl" @resolve="getPolicyInformation" />
      <SuccessInsuredWidget
        v-else-if="!error"
        :type-of-travel="lastFlowDirection"
        :email="email"
        :policy-link="policyLink"
        :cyber-policy-link="cyberPolicyLink"
        :policy-number="policyNumber"
        :is-user-registered="isUserRegistered"
        :earned-bonuses="earnedBonuses"
        :is-ooredoo-banner="isOoredooBannerEnabled"
        :is-travel-home-offer-banner="isTravelHomeOfferBannerVisible"
        :home-promocode="homePromocode"
        :is-hotel-banner="isHotelBannerEnabled"
      />
    </template>
  </FlowWidget>
</template>
